import { Form, Layout } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { SDetailLayout } from 'app/components/StaycationUI';
import { useFinishEditoEventForm } from 'app/hooks/data/edito/events/useFinishEditoEventForm';
import { useGetEditoEventDetails } from 'app/hooks/data/edito/events/useGetEditoEventDetails';
import type { IEditoEventForm } from 'app/typings/edito';
import { isDefined } from 'app/utils/typing';

import { EditoEndingBlock } from '../../commons/Detail/EditoSections/EditoEndingBlock/EditoEndingBlock';
import { EditoSectionsList } from '../../commons/Detail/EditoSections/EditoSectionsList/EditoSectionsList';

import './EditoEventDetail.scss';
import { EditoEventFooter } from './Footer/EditoEventFooter';
import { EditoEventLocation } from './Location/EditoEventLocation';
import { EditoEventMainInformation } from './MainInformation/EditoEventMainInformation';
import { EditoEventPublication } from './Publication/EditoEventPublication';

export const createEmptySection = (): IEditoEventForm['sections'][number] => ({
  tempId: crypto.randomUUID(),
  title: '',
  actionType: 'empty',
  sectionType: 'body',
  priority: 0,
});
export const emptyEndingBlock: IEditoEventForm['endingBlock'] = {
  title: '',
  actionType: 'empty',
  sectionType: 'footer',
  priority: 0,
};

export const EditoEventDetail = () => {
  const { id: paramId } = useParams<{ id: string }>();
  const parsedParamId = parseInt(paramId);
  const editoEventId = isNaN(parsedParamId) ? undefined : parsedParamId;
  const [form] = Form.useForm<IEditoEventForm>();
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);
  const title = Form.useWatch('title', form);
  const [isDirty, setIsDirty] = useState(false);

  const { initialEditoEventForm } = useGetEditoEventDetails(editoEventId);

  const {
    locationRef,
    onFinish,
    onFinishFailed,
    saveDraft,
    initialEditoStatus,
  } = useFinishEditoEventForm({
    form,
    initialEditoEventForm,
  });

  const isCreatingEditoEvent = !isDefined(initialEditoEventForm);

  const initialValues = initialEditoEventForm ?? {
    sections: [createEmptySection()],
    endingBlock: emptyEndingBlock,
  };

  useEffect(() => {
    if (initialEditoEventForm) {
      form.setFieldsValue(initialEditoEventForm);
      setIsDirty(false);
    }
  }, [initialEditoEventForm, form]);

  return (
    <Layout className="editoEventDetailLayout">
      <SDetailLayout
        mode="edit"
        isDirty={isDirty}
        title={isCreatingEditoEvent ? 'New event' : 'Edit event'}
        customFooterButtons={
          <EditoEventFooter
            form={form}
            initialEditoStatus={initialEditoStatus}
            editoTitle={title}
            endDate={endDate}
            startDate={startDate}
            submitForm={form.submit}
            saveDraft={saveDraft}
            isDirty={isDirty}
          />
        }
      >
        <Content className="content-container">
          <Form
            form={form}
            initialValues={initialValues}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            onValuesChange={() => setIsDirty(true)}
            scrollToFirstError={{
              behavior: 'smooth',
              inline: 'center',
              block: 'center',
            }}
          >
            <div className="container">
              <div ref={locationRef}>
                <EditoEventLocation form={form} />
              </div>
              <EditoEventMainInformation
                currentTitle={title}
                initialEditoEventForm={initialEditoEventForm}
              />
              <EditoSectionsList form={form} />
              <EditoEndingBlock form={form} />
              <EditoEventPublication startDate={startDate} endDate={endDate} />
            </div>
          </Form>
        </Content>
      </SDetailLayout>
    </Layout>
  );
};
